import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Switch from '@mui/material/Switch';
import FormControlLabel from '@mui/material/FormControlLabel';
import Divider from '@mui/material/Divider';
import Alert from '@mui/material/Alert';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { useTheme } from '../contexts/ThemeContext';
import { useUser } from '../contexts/UserContext';
import Sidebar from './Sidebar';
import Loading from './Loading';
import { supabase } from '../lib/supabase';

export function Settings() {
  const { profile, refresh } = useUser();
  const { mode, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState('');
  const [error, setError] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [settings, setSettings] = useState({
    emailNotifications: true,
    taskReminders: true,
    goalUpdates: true,
    forumNotifications: true,
    marketingEmails: false,
  });

  useEffect(() => {
    const fetchSettings = async () => {
      if (!profile) return;
      try {
        const { data, error } = await supabase
          .from('user_settings')
          .select('*')
          .eq('user_id', profile.id)
          .single();

        if (error) throw error;
        if (data) {
          setSettings({
            emailNotifications: data.email_notifications ?? true,
            taskReminders: data.task_reminders ?? true,
            goalUpdates: data.goal_updates ?? true,
            forumNotifications: data.forum_notifications ?? true,
            marketingEmails: data.marketing_emails ?? false,
          });
        }
      } catch (err) {
        console.error('Error fetching settings:', err);
      }
    };

    fetchSettings();
  }, [profile]);

  if (!profile) return <Loading />;

  const handleSave = async () => {
    setLoading(true);
    setSuccess('');
    setError('');

    try {
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: profile.id,
          email_notifications: settings.emailNotifications,
          task_reminders: settings.taskReminders,
          goal_updates: settings.goalUpdates,
          forum_notifications: settings.forumNotifications,
          marketing_emails: settings.marketingEmails,
          updated_at: new Date().toISOString(),
        });

      if (error) throw error;
      setSuccess('Settings saved successfully!');
      refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while saving settings');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAccount = async () => {
    setLoading(true);
    setError('');
    try {
      // Delete user settings first
      const { error: settingsError } = await supabase
        .from('user_settings')
        .delete()
        .eq('user_id', profile.id);

      if (settingsError) throw settingsError;

      // Delete user profile
      const { error: profileError } = await supabase
        .from('profiles')
        .delete()
        .eq('id', profile.id);

      if (profileError) throw profileError;

      // Delete user account
      const { error: authError } = await supabase.auth.admin.deleteUser(profile.id);
      if (authError) throw authError;

      // Sign out and redirect to login
      await supabase.auth.signOut();
      navigate('/login');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while deleting your account');
      setLoading(false);
    }
  };

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh', bgcolor: 'background.default' }}>
      <Sidebar user={profile} />
      <Box sx={{ flexGrow: 1, p: 3 }}>
        <Card sx={{ maxWidth: 800, mx: 'auto', borderRadius: 3, boxShadow: 3 }}>
          <CardHeader
            title={
              <Typography 
                variant="h4" 
                fontWeight={800} 
                sx={{ 
                  background: 'linear-gradient(45deg, #2563eb, #4f46e5)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  color: 'transparent',
                  mb: 0.5
                }}
              >
                Settings
              </Typography>
            }
            subheader={
              <Typography 
                variant="body1" 
                color="text.secondary"
                sx={{ 
                  fontSize: '1rem',
                  opacity: 0.8,
                  letterSpacing: '0.01em'
                }}
              >
                Manage your account preferences and settings
              </Typography>
            }
          />
          <CardContent>
            <Box display="flex" flexDirection="column" gap={4}>
              {/* Theme Settings */}
              <Box>
                <Typography variant="h6" fontWeight={600} mb={2} color="text.primary">
                  Appearance
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      checked={mode === 'dark'}
                      onChange={toggleTheme}
                      sx={{
                        '& .MuiSwitch-switchBase.Mui-checked': {
                          color: 'primary.main',
                          '& + .MuiSwitch-track': {
                            backgroundColor: 'primary.main',
                          },
                        },
                      }}
                    />
                  }
                  label={
                    <Typography variant="body1" color="text.primary">
                      Dark Mode
                    </Typography>
                  }
                />
              </Box>

              <Divider />

              {/* Notification Settings */}
              <Box>
                <Typography variant="h6" fontWeight={600} mb={2} color="text.primary">
                  Notifications
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.emailNotifications}
                        onChange={(e) => setSettings({ ...settings, emailNotifications: e.target.checked })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                            '& + .MuiSwitch-track': {
                              backgroundColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" color="text.primary">
                        Email Notifications
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.taskReminders}
                        onChange={(e) => setSettings({ ...settings, taskReminders: e.target.checked })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                            '& + .MuiSwitch-track': {
                              backgroundColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" color="text.primary">
                        Task Reminders
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.goalUpdates}
                        onChange={(e) => setSettings({ ...settings, goalUpdates: e.target.checked })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                            '& + .MuiSwitch-track': {
                              backgroundColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" color="text.primary">
                        Goal Updates
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.forumNotifications}
                        onChange={(e) => setSettings({ ...settings, forumNotifications: e.target.checked })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                            '& + .MuiSwitch-track': {
                              backgroundColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" color="text.primary">
                        Forum Notifications
                      </Typography>
                    }
                  />
                  <FormControlLabel
                    control={
                      <Switch
                        checked={settings.marketingEmails}
                        onChange={(e) => setSettings({ ...settings, marketingEmails: e.target.checked })}
                        sx={{
                          '& .MuiSwitch-switchBase.Mui-checked': {
                            color: 'primary.main',
                            '& + .MuiSwitch-track': {
                              backgroundColor: 'primary.main',
                            },
                          },
                        }}
                      />
                    }
                    label={
                      <Typography variant="body1" color="text.primary">
                        Marketing Emails
                      </Typography>
                    }
                  />
                </Box>
              </Box>

              <Divider />

              {/* Account Settings */}
              <Box>
                <Typography variant="h6" fontWeight={600} mb={2} color="text.primary">
                  Account
                </Typography>
                <Box display="flex" flexDirection="column" gap={2}>
                  <TextField
                    label="Email"
                    value={profile.email}
                    disabled
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                      },
                    }}
                  />
                  <Button
                    variant="outlined"
                    color="error"
                    onClick={() => setDeleteDialogOpen(true)}
                    sx={{
                      borderRadius: 2,
                      mt: 1,
                    }}
                  >
                    Delete Account
                  </Button>
                </Box>
              </Box>

              {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}
              {success && <Alert severity="success" sx={{ borderRadius: 2 }}>{success}</Alert>}

              <Button
                variant="contained"
                onClick={handleSave}
                disabled={loading}
                sx={{
                  fontWeight: 600,
                  fontSize: '1rem',
                  py: 1.2,
                  borderRadius: 2,
                  background: 'linear-gradient(45deg, #2563eb, #4f46e5)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #1d4ed8, #4338ca)',
                  }
                }}
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          </CardContent>
        </Card>
      </Box>

      {/* Delete Account Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" fontWeight={600}>
            Delete Account
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            Are you sure you want to delete your account? This action cannot be undone. All your data will be permanently deleted.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteDialogOpen(false)}
            sx={{ borderRadius: 2 }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAccount}
            color="error"
            variant="contained"
            disabled={loading}
            sx={{ borderRadius: 2 }}
          >
            {loading ? 'Deleting...' : 'Delete Account'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
} 